import { Box, Card, CardContent, Typography } from '@mui/material';
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium';
import { IsTenantAdministrator } from 'shared/permissions/IsTenantAdministrator';
import { IContractEquipment } from 'features/inventory/contracts/types';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { contractEquipmentService } from 'shared/services';
import { IEquipment } from '../../types';
import { DataGridLinkCell, booleanCell } from 'components/cells';
import { SectionHeader } from 'components/ui/typography';

/** Props for the contract equipment tab. */
interface ContractEquipmentTabProps {
  equipment: IEquipment;
}

/**
 * Tab that lists active contracts linked to a given equipment.
 *
 * Data is fetched paginated and filtered by the equipment identifier.
 * The grid is rendered only for tenant administrators.
 */
export const ContractEquipmentTab = ({ equipment }: ContractEquipmentTabProps) => {
  const { t } = useTranslation();

  const [contractEquipments, setContractEquipments] = useState<IContractEquipment[]>([]);
  const [contractLoading, setContractLoading] = useState(false);

  /* ------------------ Data fetch ------------------ */

  useEffect(() => {
    setContractLoading(true);
    contractEquipmentService
      .getAllPaginated(100, 0, { equipment_id: equipment.id, active: true })
      .then((res) => {
        setContractEquipments(res.results);
      })
      .catch(() => setContractEquipments([]))
      .finally(() => setContractLoading(false));
  }, [equipment]);

  /* ------------------ Columns ------------------ */

  const contractColumns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'contract_header_code',
        headerName: t('entities.contract-equipment.contract_code'),
        flex: 1,
        minWidth: 200,
        renderCell: (params) => (
          <DataGridLinkCell
            params={params}
            basePath="/tenant/contracts"
            idKey="contract_header"
            passDuplicateState
          />
        )
      },
      {
        field: 'maintenance_open',
        headerName: t('entities.contract-equipment.maintenance_open'),
        flex: 1,
        minWidth: 200,
        type: 'boolean',
        renderCell: booleanCell(t)
      },
      {
        field: 'ticketing_open',
        headerName: t('entities.contract-equipment.ticketing_open'),
        flex: 1,
        minWidth: 200,
        type: 'boolean',
        renderCell: booleanCell(t)
      }
    ],
    [t]
  );

  /* ------------------ Render ------------------ */

  return (
    <>
      <IsTenantAdministrator>
        <Box sx={{ mb: 3 }}>
          <SectionHeader
            title={t('global.contracts')}
            subtitle="Consulta i contratti attivi associati per questo strumento con stato di manutenzione e ticketing attivo"
          />
          <Card variant="outlined" sx={{ mt: 2 }}>
            <CardContent>
              {contractLoading ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                  Caricamento…
                </Typography>
              ) : contractEquipments.length != 0 ? (
                <DataGridPremium
                  density="compact"
                  rows={contractEquipments}
                  columns={contractColumns}
                  sx={{ border: 'none' }}
                  disableRowSelectionOnClick
                />
              ) : (
                <Typography color="text.secondary">
                  {t('entities.equipment.noContracts', 'Nessun contratto presente.')}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Box>
      </IsTenantAdministrator>
    </>
  );
};
